/**
 * The story's fills, from home-ceo-mobile `wee-sequence.tsx`.
 *
 * Each fill is a vertical ramp across the mark's ink — pale at the ends,
 * deep in the middle, the shape the brand guide requires of the mark —
 * resampled onto five even stops so any two can be cross-faded channel
 * by channel. That is what lets one drawn house travel light → its own
 * colour → lit gold → white instead of stacking copies that fade over
 * each other.
 */

import { colors } from '@config/colors';
import { rgba } from '@components/water/water-field';

export type Rgb = [number, number, number];
export type Ramp = Rgb[];

export const STOP_COUNT = 5;
/** Offsets of the five stops a ramp is drawn with. */
export const STOP_OFFSETS = [0, 0.25, 0.5, 0.75, 1] as const;

function toRgb(color: string): Rgb {
  const [r, g, b] = rgba(color);
  return [r * 255, g * 255, b * 255];
}

/** Resample a ramp onto `STOP_COUNT` even stops, preserving its shape. */
export function resample(stops: readonly string[], positions?: readonly number[]): Ramp {
  const rgb = stops.map(toRgb);
  const pos = positions ?? stops.map((_, i) => i / (stops.length - 1));
  const out: Ramp = [];
  for (let s = 0; s < STOP_COUNT; s++) {
    const at = s / (STOP_COUNT - 1);
    let j = 0;
    while (j < pos.length - 2 && pos[j + 1] < at) j++;
    const span = pos[j + 1] - pos[j] || 1;
    const k = Math.max(0, Math.min(1, (at - pos[j]) / span));
    out.push([0, 1, 2].map((c) => rgb[j][c] + (rgb[j + 1][c] - rgb[j][c]) * k) as Rgb);
  }
  return out;
}

/**
 * The colours of a house at `phase` along `ramps`: cross-fades the two
 * ramps the phase sits between. Returned as CSS strings, one per stop.
 */
export function rampAt(ramps: readonly Ramp[], phase: number): string[] {
  const p = Math.max(0, Math.min(ramps.length - 1, phase));
  const lo = Math.floor(p);
  const hi = Math.min(ramps.length - 1, lo + 1);
  const k = p - lo;
  return ramps[lo].map((a, i) => {
    const b = ramps[hi][i];
    const c = [0, 1, 2].map((ch) => Math.round(a[ch] + (b[ch] - a[ch]) * k));
    return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  });
}

const { accent, wee } = colors;

/** The lantern: light, so its middle is the brightest point. */
export const RAMP_LANTERN = resample(
  [wee.lanternEdge, wee.lanternCore, wee.lanternCore, wee.lanternCore, wee.lanternEdge],
  [0, 0.2, 0.5, 0.8, 1],
);

/** The house lit from within as it comes alive: pale gold, a step lighter than amber. */
export const RAMP_LIT = resample([accent.yellow.light, accent.yellow.base, accent.yellow.light]);

/** The glow from within, drawn blurred behind the house. */
export const RAMP_GLOW = resample([accent.yellow.base, accent.yellow.dark, accent.yellow.base]);

/** The Explore card at rest, as the mock draws it: periwinkle, lavender at the foot. */
export const RAMP_EXPLORE = resample(
  [wee.exploreHouse, wee.exploreHouse, wee.exploreHouseFoot],
  [0, 0.55, 1],
);

/** The Enact card at rest: gold, amber in the middle. */
export const RAMP_ENACT = resample([accent.yellow.dark, accent.yellow.extraDark, accent.yellow.dark]);

/**
 * The five options, each in its own family so they read as options —
 * the brand guide's five-series set in the app's order: purple, blue,
 * green, amber, pink. Amber stays at index 3 so `CHOSEN` still picks it,
 * and pink and blue (the pair a protanope confuses) are not neighbours.
 */
export const OPTIONS = [
  { pale: accent.purple.light, deep: accent.purple.dark },
  { pale: accent.blue.base, deep: accent.blue.extraDark },
  { pale: accent.green.dark, deep: accent.green.exxtraDark },
  { pale: accent.yellow.dark, deep: accent.yellow.exxtraDark },
  { pale: accent.pink.base, deep: accent.pink.extraDark },
] as const;

/** Which option wins — the amber one. Fixed, so the story is the same every cycle. */
export const CHOSEN = 3;

export const OPTION_RAMPS = OPTIONS.map((o) => resample([o.pale, o.deep, o.pale]));

/**
 * The colour that drifts across a house before it takes its own: soft
 * accents in a sweep wider than the mark, moving, so neighbouring houses
 * sample different parts of it.
 */
export const TINT_STOPS = [
  accent.pink.dark,
  accent.purple.base,
  accent.blue.base,
  accent.coral.dark,
  accent.pink.dark,
] as const;
export const TINT_PEAK = 0.9;
